import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    // Initialize variant selection for pre-selected variants (e.g., from URL parameters)
    this.#initializeVariantSelection();
  }

  /**
   * Initializes variant selection when the page loads with a pre-selected variant.
   * This handles cases where the user arrives at the product page with a variant parameter in the URL.
   */
  #initializeVariantSelection() {
    // Only run on product pages, not in product cards or quick add dialogs
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    // Check if there's a variant parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');

    if (variantParam) {
      // Find the selected option that corresponds to this variant
      const selectedOption = this.selectedOption;

      if (selectedOption) {
        // Trigger the variant update logic without the actual change event
        this.#triggerVariantUpdate(selectedOption);
      }
    }
  }

  /**
   * Triggers variant update logic for a given option element.
   * This is used both for manual changes and initial page load.
   * @param {HTMLElement} selectedElement - The selected option element.
   */
  #triggerVariantUpdate(selectedElement) {
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedElement.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !selectedElement.closest('product-card') &&
      !selectedElement.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedElement.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Check if we can use fast variant selection
    const canUseFastSelection = this.#canUseFastVariantSelection(loadsNewProduct);

    // DEBUG: Log which path is being used
    console.log('[VARIANT DEBUG] Fast selection enabled:', canUseFastSelection);
    console.log('[VARIANT DEBUG] Theme.settings.enable_fast_variant_selection:', Theme?.settings?.enable_fast_variant_selection);

    if (canUseFastSelection) {
      console.log('[VARIANT DEBUG] Using FAST variant selection (client-side)');
      this.#handleFastVariantSelection(selectedElement);
    } else {
      console.log('[VARIANT DEBUG] Using SLOW variant selection (server fetch)');
      this.fetchUpdatedSection(this.buildRequestUrl(selectedElement), loadsNewProduct);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);

    // Use the shared variant update logic
    this.#triggerVariantUpdate(event.target);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Determines if fast variant selection can be used.
   * @param {boolean} loadsNewProduct - Whether a new product is being loaded (combined listings).
   * @returns {boolean} Whether fast variant selection can be used.
   */
  #canUseFastVariantSelection(loadsNewProduct) {
    // Must have fast variant selection enabled
    if (!Theme?.settings?.enable_fast_variant_selection) {
      return false;
    }

    // Cannot use for combined listings (different products)
    if (loadsNewProduct) {
      return false;
    }

    // Must have product data available
    if (!Theme?.product?.variants) {
      return false;
    }

    // Must be on product page template
    const templateName = Theme?.template?.name;
    if (!templateName || !templateName.includes('product')) {
      return false;
    }

    return true;
  }

  /**
   * Handles fast variant selection using client-side data.
   * @param {HTMLElement} selectedElement - The selected option element.
   */
  #handleFastVariantSelection(selectedElement) {
    try {
      // Find the matching variant based on selected options
      const selectedVariant = this.#findSelectedVariant();

      if (selectedVariant) {
        // Update the JSON script with new variant data
        this.#updateVariantScript(selectedVariant);

        // Create updated price HTML for fast selection
        const updatedDocument = this.#createUpdatedDocument(selectedVariant);

        // Dispatch the variant update event
        this.dispatchEvent(
          new VariantUpdateEvent(selectedVariant, this.selectedOptionId, {
            html: updatedDocument, // Use document with updated price
            productId: this.dataset.productId ?? '',
            newProduct: undefined, // No new product for fast selection
          })
        );
      } else {
        this.fetchUpdatedSection(this.buildRequestUrl(selectedElement), false);
      }
    } catch (error) {
      console.warn('Fast variant selection failed, falling back to server request:', error);
      // Fallback to server request
      this.fetchUpdatedSection(this.buildRequestUrl(selectedElement), false);
    }
  }

  /**
   * Finds the variant that matches currently selected options.
   * @returns {object | null} The matching variant or null if not found.
   */
  #findSelectedVariant() {
    try {
      const variants = Theme?.product?.variants;
      if (!variants) return null;

      const selectedOptionsValues = this.selectedOptionsValues;
      const optionsWithValues = Theme?.product?.options_with_values || [];

    // Build selected options map by finding the actual option values
    const selectedOptions = {};
    selectedOptionsValues.forEach((optionValueId, index) => {
      const option = optionsWithValues[index];
      if (!option) return;

      // Find the selected element using the data-option-value-id attribute
      const selectedElement = document.querySelector(`input[data-option-value-id="${optionValueId}"]`);

      if (selectedElement) {
        const selectedValue = selectedElement.value;

        // Match this with the available values
        const matchedValue = option.values.find(val => val === selectedValue);

        if (matchedValue) {
          selectedOptions[`option${index + 1}`] = matchedValue;
        }
      }
    });

    // Find matching variant
    const matchedVariant = variants.find(variant => {
      const match1 = !selectedOptions.option1 || variant.option1 === selectedOptions.option1;
      const match2 = !selectedOptions.option2 || variant.option2 === selectedOptions.option2;
      const match3 = !selectedOptions.option3 || variant.option3 === selectedOptions.option3;
      return match1 && match2 && match3;
    });

    return matchedVariant;
    } catch (error) {
      console.error('❌ Error in variant matching:', error);
      return null;
    }
  }

  /**
   * Detects the show_sale_price_first setting by analyzing the current DOM structure.
   * @param {Element} priceContainer - The price container element.
   * @returns {boolean} - True if sale price should be shown first.
   */
  #detectShowSalePriceFirst(priceContainer) {
    // Check the order of current-price-group and compare-price-group elements
    const currentPriceGroup = priceContainer.querySelector('.current-price-group');
    const comparePriceGroup = priceContainer.querySelector('.compare-price-group');

    // If both elements exist, check which comes first
    if (currentPriceGroup && comparePriceGroup) {
      // Get all child elements and find the index of each group
      const children = Array.from(priceContainer.children);
      const currentPriceIndex = children.indexOf(currentPriceGroup);
      const comparePriceIndex = children.indexOf(comparePriceGroup);

      console.log('[DETECTION DEBUG] currentPriceIndex:', currentPriceIndex, 'comparePriceIndex:', comparePriceIndex);
      
      // If compare price comes before current price, then show_sale_price_first is true
      const result = comparePriceIndex < currentPriceIndex;
      console.log('[DETECTION DEBUG] Detected showSalePriceFirst:', result);
      return result;
    }

    console.log('[DETECTION DEBUG] Could not find both price groups, defaulting to true');
    // Default to true if we can't determine from DOM structure
    return true;
  }

  /**
   * Creates a document with updated price information for fast selection.
   * @param {object} variant - The selected variant data.
   * @returns {Document} - Document with updated price HTML.
   */
  #createUpdatedDocument(variant) {
    // Clone the current document
    const clonedDocument = document.cloneNode(true);

    // Find the price container in the cloned document
    const priceContainer = clonedDocument.querySelector('product-price [ref="priceContainer"]');

    if (priceContainer && variant.price !== undefined) {
      // Determine if this variant has a discount
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

      // Format prices
      const formattedPrice = this.#formatPrice(variant.price);
      const formattedComparePrice = hasDiscount ? this.#formatPrice(variant.compare_at_price) : null;

      // Calculate savings percentage
      let savingsPercentage = 0;
      if (hasDiscount) {
        const savingsAmount = variant.compare_at_price - variant.price;
        savingsPercentage = Math.round((savingsAmount * 100) / variant.compare_at_price);
      }

      // Recreate the complete price HTML structure
      let priceHTML = '';

      // Detect the current show_sale_price_first setting by checking the DOM structure
      const showSalePriceFirst = this.#detectShowSalePriceFirst(priceContainer);
      
      console.log('[PRICE DEBUG] Detected showSalePriceFirst:', showSalePriceFirst);

      if (showSalePriceFirst) {
        console.log('[PRICE DEBUG] Building HTML: strikethrough first, then sale');
        // Show strikethrough price first, then sale price
        if (hasDiscount) {
          priceHTML += `<span role="group" class="compare-price-group">`;
          priceHTML += `<span class="visually-hidden">Regular price&nbsp;</span>`;
          priceHTML += `<span class="compare-at-price"><span class="money">${formattedComparePrice}</span></span>`;
          priceHTML += `</span>`;
        }

        priceHTML += `<span role="group" class="current-price-group">`;
        if (hasDiscount) {
          priceHTML += `<span class="visually-hidden">Sale price&nbsp;</span>`;
        }
        priceHTML += `<span class="price">${formattedPrice}</span>`;
        priceHTML += `</span>`;
      } else {
        console.log('[PRICE DEBUG] Building HTML: sale first, then strikethrough');
        // Show sale price first, then strikethrough price
        priceHTML += `<span role="group" class="current-price-group">`;
        if (hasDiscount) {
          priceHTML += `<span class="visually-hidden">Sale price&nbsp;</span>`;
        }
        priceHTML += `<span class="price">${formattedPrice}</span>`;
        priceHTML += `</span>`;

        if (hasDiscount) {
          priceHTML += `<span role="group" class="compare-price-group">`;
          priceHTML += `<span class="visually-hidden">Regular price&nbsp;</span>`;
          priceHTML += `<span class="compare-at-price"><span class="money">${formattedComparePrice}</span></span>`;
          priceHTML += `</span>`;
        }
      }

      // Add savings badge if there's a discount
      if (hasDiscount && savingsPercentage > 0) {
        priceHTML += `<span class="savings-badge">Saving ${savingsPercentage}%</span>`;
      }

      // Preserve any existing star rating or other elements
      const existingStarRating = priceContainer.querySelector('.price-star-rating');
      if (existingStarRating) {
        priceHTML += existingStarRating.outerHTML;
      }

      // Update the price container with the new HTML
      priceContainer.innerHTML = priceHTML;
    }

    return clonedDocument;
  }

  /**
   * Formats a price value (in cents) to a display string.
   * @param {number} priceInCents - Price in cents.
   * @returns {string} - Formatted price string.
   */
  #formatPrice(priceInCents) {
    // Get currency and decimal separator from existing price element to match format
    const existingPrice = document.querySelector('.price');
    if (existingPrice) {
      const existingText = existingPrice.textContent;
      const currencyMatch = existingText.match(/[€£$¥¢]/);
      const currency = currencyMatch ? currencyMatch[0] : '€';

      // Check if existing price uses comma as decimal separator
      const usesCommaDecimal = existingText.includes(',') && !existingText.includes('.');

      // Convert cents to currency units and format with appropriate decimal separator
      const price = (priceInCents / 100).toFixed(2);
      const formattedPrice = usesCommaDecimal ? price.replace('.', ',') : price;

      return `${currency}${formattedPrice}`;
    }

    // Fallback formatting with comma decimal separator
    const price = (priceInCents / 100).toFixed(2).replace('.', ',');
    return `€${price}`;
  }

  /**
   * Updates the variant script tag with new variant data.
   * @param {object} variant - The new variant data.
   */
  #updateVariantScript(variant) {
    const scriptTag = this.querySelector('script[type="application/json"]');
    if (scriptTag) {
      scriptTag.textContent = JSON.stringify(variant);
    }
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
